import type { BranchQuote, BranchQuoteSupport } from '../../../api/dealer-api-interface-franchisee';
import { BranchQuoteSupportItemType } from '../../../api/dealer-api-interface-franchisee';
import type { SSEBranchQuoteFabricationLock } from '../../../api/fabrication-job-api';
import { fabricatedStandardFrameContentType } from '../../../api/fabrication-job-api';
import { WMEventSource } from '../../../api/event-source';
import { cache } from '../../cache-impl/cache-registry';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import { currentUserClaims } from '../../../../webmodule-common/other/currentuser-claims';
import { customElement, state } from 'lit/decorators.js';
import { DealerReport } from '../../../api/dealer-api-interface-clientagent';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error';
import { DocumentSubType, ResourceType } from '../../../api/dealer-api-interface-project';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
// Switched to merged ETO view component
import { EngineeredToOrderLineItemViewMerged } from './franchisee-engineered-to-order-merged';
import { fireQuickErrorToast } from '../../../../webmodule-common/other/toast-away';
import { flagInSet, isEmptyOrSpace, validId } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { FranchiseeQuoteCalculationsView } from './franchisee-quote-calculations-view';
import { FranchiseeQuoteContainer } from '../data/franchisee-quote-container';
import { FranchiseeQuoteDetailView } from './franchisee-quote-detail-view';
import { userDataStore } from '../../common/current-user-data-store';
import { FranchiseeQuoteItemFrameViewForV6 } from './v6/franchisee-v6-quote-item-frame-view';
import { FranchiseeQuoteItemsView } from './franchisee-quote-items-view';
import { FranchiseeQuoteSupportTicketView } from './franchisee-support-ticket-editor-view';
import { FranchiseeQuoteTermsView } from './franchisee-quote-terms-view';
import { FranchiseeV6QuoteSupplierDefaultsView } from './v6/franchisee-v6-quote-defaults-view';
import { FranchiseeV6QuoteSupplierGlassView } from './v6/franchisee-v6-supplier-quote-glass-view';
import { getApiFactory } from '../../../api/api-injector';
import { getPermanentProjectDocumentURL } from '../../cache-impl/project-permanent-document-lookup';
import { getQuoteStateEngine } from '../data/quote-state-engine';
import { getQuoteSuppliers, isMultiSupplier } from '../../../quotes/quote-service';
import {
  getQuoteTitle,
  isDealerQuotePriceAdjustment,
  isFrame,
  isSpecialItem,
  isSupplierFrameItem
} from '../../../quotes/data/quote-helper-functions';
import {
  fetchQuoteAsset,
  getV6QuoteItemForContainer,
  isSupplierUsingSSI,
  splitV6QuoteItemAfterCommit
} from '../../../quotes/data/v6/helper-functions';
import { goURL, resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import type { TemplateResult } from 'lit';
import { html } from 'lit';
import { information } from '../../../../webmodule-common/other/ui/modal-option';
import { InformationDispatcher } from '../../../../webmodule-common/other/ui/information-dispatcher';
import {
  isValidV6ConfigVersion,
  v6Config,
  v6Editors,
  v6SupportsVersion,
  v6VersionMap
} from '../../../../webmodule-common/interop/interfaces/v6-config';
import { LangFranchiseeQuoteView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-view-lang';
import type { QuoteViewChildFactory, QuoteViewOptions } from '../../../quotes/views/quote-view';
import { QuoteDataEntryView, QuoteView, QuoteViewChildFactoryImpl } from '../../../quotes/views/quote-view';
import { quoteItemAction } from '../../../quotes/data/events';
import { quoteItemContentType } from '../../../quotes/data/default-quote-item-content-type';
// OpenTicketHandler removed with inline support wrapper; direct event listener used instead
import type { Quote, QuoteItem } from '../../../api/dealer-api-interface-quote';
import { QuoteState } from '../../../api/dealer-api-interface-quote';
import { resourceProject } from '../../../projects/ui/launcher';
import { resourcePurchaseOrder } from '../../../purchase-orders/ui/launcher';
import { SaveWorkflowModal } from '../../../../webmodule-common/other/save-workflow';
import { supplierQuoteItemContentType } from '../../../quotes/data/supplier-quote-item-content-type';
import { V6OutOfDateQuoteItemHandler } from '../data/v6/out-of-date-quote-item-handler';
import { V6OutOfDateQuoteItemHandlerSafeMode } from '../data/v6/out-of-date-quote-item-handler-safe-mode';
import { V6SupplierQuoteBase } from '../../../quotes/data/v6/v6-supplier-quote';
import { validateOutOfDateQuoteItems } from '../../../quotes/views/quote-item-validation-modal';
import { WebModuleQuoteSupportInboxView } from '../../conversation/wm-branchquote-support';
import type { DataEntryOwner } from '../../../../webmodule-common/other/ui/DataEntryOwner';
import type { FranchiseeQuoteContainerManager, FranchiseeQuoteManagerUiEvent } from '../data/franchisee-quote-manager';

import type { IQuoteValidator } from '../../../quotes/data/quote-container';
import type { IUserQuoteStateEngine } from '../../../quotes/data/quote-state-engine';
import type {
  TabAction,
  TabCloseEvent,
  TabPage
} from '../../../../webmodule-common/other/ui/tab-manager/tab-manager-types';
import type {
  NullPromise,
  QuoteFrameItemMarker,
  QuoteItemsProvider,
  Snippet,
  V6CustomItemRequestType
} from '../../../../webmodule-common/interop/webmodule-interop';
import { QuoteItemState } from '../../../api/dealer-api-interface-all';
import type { QuoteDetailView, QuoteDetailViewOptions } from '../../../quotes/views/quote-detail-view';
import type { QuoteItemContainer } from '../../../quotes/data/quote-item-container';
import type { QuoteItemsView, QuoteItemsViewOptions } from '../../../quotes/views/quote-items-view';
import type { QuoteItemView, QuoteItemViewOptions } from '../../../quotes/views/quote-item-view';
import type {
  QuoteSupplierDefaultsView,
  QuoteSupplierDefaultsViewOptions
} from '../../../quotes/views/quote-supplier-defaults.view';
import type {
  QuoteSupplierGlassView,
  QuoteSupplierGlassViewOptions
} from '../../../quotes/views/quote-supplier-glass-view';

import { AskConfirmation } from '../../../../webmodule-common/other/ui/modal-confirmation';
import { getSupportTypeStatus } from '../../conversation/conversation-helper';
import { SupportTicketService } from '../../conversation/support-ticket-service';

export class FranchiseeQuoteViewChildFactory extends QuoteViewChildFactoryImpl {
  protected get view(): FranchiseeQuoteDEView {
    return this.parent as FranchiseeQuoteDEView;
  }

  getDetailView(options: QuoteDetailViewOptions): QuoteDetailView {
    return new FranchiseeQuoteDetailView({
      quoteManager: options.quoteManager
    });
  }

  getItemsView(options: QuoteItemsViewOptions): QuoteItemsView {
    return new FranchiseeQuoteItemsView(options);
  }

  getSupplierDefaultsView(options: QuoteSupplierDefaultsViewOptions): QuoteSupplierDefaultsView {
    if (options.quoteManager.isV6) return new FranchiseeV6QuoteSupplierDefaultsView(options);
    //V7?
    return super.getSupplierDefaultsView(options);
  }

  getSupplierGlassView(options: QuoteSupplierGlassViewOptions): QuoteSupplierGlassView {
    if (options.quoteManager.isV6) return new FranchiseeV6QuoteSupplierGlassView(options);
    //V7?
    return super.getSupplierGlassView(options);
  }

  getSupplierQuoteItemView(
    contentType: number,
    options: QuoteItemViewOptions,
    supplierSelector?: unknown
  ): QuoteItemView {
    if (options.quoteManager.isV6) {
      if (contentType === supplierQuoteItemContentType.CID_FRAM)
        return new FranchiseeQuoteItemFrameViewForV6(options, supplierSelector);
    }
    return super.getSupplierQuoteItemView(contentType, options, supplierSelector);
  }
}

/**
 * abstract out logic that is driven by the supplier provider type, so that we can clean and seperate this logic out into
 * a factory injection
 */
export class QuoteSupplierProviderInterface {
  async mustValidateItems(qcm: FranchiseeQuoteContainerManager): Promise<boolean> {
    return await qcm.currentUserMustValidate();
  }

  async pricingNeedsUpdate(qcm: FranchiseeQuoteContainerManager): Promise<boolean> {
    return qcm.pricingMustBeUpdated;
  }

  /**
   * override this to add logic
   */
  async validateOutOfDateQuoteItems(qcm: FranchiseeQuoteContainerManager) {
    await validateOutOfDateQuoteItems(qcm, new V6OutOfDateQuoteItemHandler(qcm));
  }

  async reValidateOutOfDateQuoteItemsOnLockedQuote(qcm: FranchiseeQuoteContainerManager) {
    const handler = new V6OutOfDateQuoteItemHandlerSafeMode(qcm);
    await validateOutOfDateQuoteItems(qcm, handler);
  }
}

@customElement('wm-franchiseequotedeview')
export class FranchiseeQuoteDEView extends QuoteDataEntryView implements IQuoteValidator {
  prlCache = cache().projectResourceLink;
  projectCache = cache().project;
  purchaseOrderCache = cache().purchaseOrder;

  protected supportList: WebModuleQuoteSupportInboxView | null;
  protected termsView: FranchiseeQuoteTermsView | null;
  protected calculationsView: FranchiseeQuoteCalculationsView | null;

  protected providerInterface: QuoteSupplierProviderInterface = new QuoteSupplierProviderInterface();
  protected projectApi = getApiFactory().project();
  protected purchaseOrderApi = getApiFactory().purchaseOrder();
  @state()
  private _orderId: string = emptyGuid;
  @state()
  private _projectId: string = emptyGuid;
  @state()
  private _projectCacheDisplay: string | undefined;
  @state()
  private _orderCacheDisplay: string | undefined;
  @state()
  private _supplierTitle: TemplateResult = html``;
  @state()
  private _canCreateShopDrawingPreview: boolean = false;
  @state()
  private _permanentShopDrawingUrl: string | null = null;
  private _reportMenuItems: TabAction[] = [];
  private _uiEventUnsub: (() => void) | null = null;

  constructor(options: QuoteViewOptions, owner: DataEntryOwner) {
    super(options, owner);

    this.supportList = new WebModuleQuoteSupportInboxView();
    this.supportList.quoteManager = this.quoteContainerManager;
    this.supportList.addEventListener('support:open-ticket', (e: Event) => this.onSupportTicketOpen(e as CustomEvent));

    this.termsView = new FranchiseeQuoteTermsView({
      quoteManager: this.quoteContainerManager
    });

    this.calculationsView = new FranchiseeQuoteCalculationsView({
      quoteManager: this.quoteContainerManager
    });

    (this.detailView as FranchiseeQuoteDetailView).onTermsAndConditionsChanged = () =>
      this.termsView?.syncTermsEditor();

    this.quoteContainerManager.quoteValidator = this.detailView;
  }

  public get quoteContainerManager(): FranchiseeQuoteContainerManager {
    return this._quoteContainerManager as FranchiseeQuoteContainerManager;
  }

  public set quoteContainerManager(value: FranchiseeQuoteContainerManager) {
    this._quoteContainerManager = value;
  }

  public get quote(): Quote {
    if (!this.quoteContainer.quote) throw new DevelopmentError('quote is null');
    return this.quoteContainer.quote;
  }

  public get quoteItems(): QuoteItem[] {
    if (!this.quoteContainer.items) throw new DevelopmentError('quote items is null');
    return this.quoteContainer.items;
  }

  private _supportTicketService?: SupportTicketService;

  private _sseFabricationLockHandler = (_data: unknown) => {
    const data = _data as SSEBranchQuoteFabricationLock | undefined;
    if (!data?.quoteId) return;
    const bq = this.branchQuote;
    if (!bq || data.quoteId !== bq.id) return;
    bq.lockForFabrication = data.lockForFabrication;
    this.requestUpdate();
  };

  connectedCallback(): void {
    super.connectedCallback();
    this._supportTicketService = this.quoteContainerManager.supportTicketService;

    const bq = this.branchQuote;
    if (this._supportTicketService && bq) {
      this._supportTicketService.attachSse({ quoteId: bq.id });
      // Note: Manager now accesses service state directly, no subscription needed
    }
    // Subscribe to manager UI events (decoupled from direct hash changes)
    this._uiEventUnsub = this.quoteContainerManager.onUiEvent((evt: FranchiseeQuoteManagerUiEvent) => {
      if (evt.type === 'show-fragment') {
        if (evt.fragment === 'support') {
          setTimeout(() => {
            // Bring support page to front; update hash for bookmarking without forcing reload
            const supportPage = this.tabManager.pages.find(p => p.fragment === 'support');
            if (supportPage) void this.tabManager.setActivePage(supportPage);
          }, 5);
        }
      }
    });
    if (userDataStore.isFabricatorMode) {
      WMEventSource.getInstance().addEventListener(
        WMEventSource.branchQuoteFabricationLock,
        this._sseFabricationLockHandler
      );
      this.quoteContainerManager.attachFabricationStatusSse();
    }
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this._supportTicketService) {
      this._supportTicketService.detachSse();
      // Note: No support service subscription to detach - manager accesses state directly
      this._supportTicketService = undefined;
    }
    if (this._uiEventUnsub) {
      try {
        this._uiEventUnsub();
      } catch {
        /* noop */
      }
      this._uiEventUnsub = null;
    }
    if (userDataStore.isFabricatorMode) {
      WMEventSource.getInstance().removeEventListener(
        WMEventSource.branchQuoteFabricationLock,
        this._sseFabricationLockHandler
      );
      this.quoteContainerManager.detachFabricationStatusSse();
    }
  }
  protected get branchQuote(): BranchQuote | null {
    if (!(this.quoteContainerManager.container instanceof FranchiseeQuoteContainer))
      throw new DevelopmentError('FranchiseeQuoteView requires a container type FranchiseeQuoteContainer');
    return this.quoteContainerManager.container.branchQuote;
  }

  protected get supportTicketPagesOpenCount(): number {
    let count = 0;
    this._tabManager?.pages.forEach(page => {
      if (page.data instanceof FranchiseeQuoteSupportTicketView) {
        count++;
      }
    });
    return count;
  }

  public async createSupportTicket(branchQuoteSupportType: BranchQuoteSupportItemType) {
    if (await this.supportTicketAlreadyOpen()) return;
    const editor = new FranchiseeQuoteSupportTicketView(this.quoteContainerManager, undefined, branchQuoteSupportType);
    const page = editor.createTabPage();
    this.tabManager.addPage(page);
  }

  public async openSupportTicket(branchQuoteSupport: BranchQuoteSupport, asSupportTicket = false) {
    if (branchQuoteSupport.type === BranchQuoteSupportItemType.EngineeredToOrder && !asSupportTicket) {
      await this.launchItemByConversationId(branchQuoteSupport.conversationId);
      return;
    }
    if (await this.focusSupportTicketIfAvailable(branchQuoteSupport)) {
      return;
    }
    if (await this.supportTicketAlreadyOpen()) return;
    const editor = new FranchiseeQuoteSupportTicketView(this.quoteContainerManager, branchQuoteSupport);
    const page = editor.createTabPage();
    this.tabManager.addPage(page);
  }

  async showVrml(): Promise<void> {
    await this.quoteContainerManager.needsQuoteItems();
    const data = () => {
      return (
        this.quoteContainerManager.container.items
          ?.filter(x => isFrame(x))
          .map(x => {
            const container = this.quoteContainerManager.quoteItemContainer(x.id);
            const qfd: QuoteFrameItemMarker = {
              id: x.id,
              description: container.item.title,
              description2: container.item.description,
              qty: container.item.quantity,
              position: this.quoteContainerManager.itemPosition(x.id) ?? -1,
              quoteItem: () => {
                return getV6QuoteItemForContainer(container);
              },
              fetchAsset: async (assetType: string): Promise<string | object | null> => {
                if (assetType === 'model3d') {
                  const legacyData = splitV6QuoteItemAfterCommit(getV6QuoteItemForContainer(container));
                  //legacy data
                  const data = legacyData.vrml;
                  if (data && typeof data === 'string' && data.trim().length > 0) {
                    return data;
                  }
                  //new data
                  const c = this.quoteContainerManager.quoteItemContainer(x.id);
                  return (
                    ((await fetchQuoteAsset({
                      quoteId: x.quoteId,
                      quoteItemId: x.id,
                      assetPath: c.data?.assetVirtualPaths['model3d']?.path,
                      publicUrl: c.data?.assetVirtualPaths['model3d']?.publicUrl
                    })) as string) ?? ''
                  );
                } else if (assetType === 'data') {
                  return getV6QuoteItemForContainer(container) as any;
                }
                return null;
              },
              hasSupplierAdjustment:
                container.price.supplierPriceAdjustmentEnabled && container.price.supplierPriceAdjustment !== 0
            };
            return qfd;
          }) ?? []
      );
    };
    const provider: QuoteItemsProvider = {
      quoteItems: () => Promise.resolve(data())
    };
    await v6Editors().runVrmlViewer(provider);
  }
  async focusSupportTicketIfAvailable(branchQuoteSupport: BranchQuoteSupport) {
    const foundPage = this.findIfSupportPageAvailable(branchQuoteSupport);
    if (foundPage) {
      await this.tabManager.setActivePage(foundPage);
      return true;
    }
    return false;
  }

  findIfSupportPageAvailable(branchQuoteSupport: BranchQuoteSupport): TabPage | undefined {
    return this._tabManager?.pages.find(page => {
      if (page.data instanceof FranchiseeQuoteSupportTicketView) {
        if (page.data.branchQuoteSupport?.id === branchQuoteSupport.id) {
          return true;
        }
      }
      return false;
    });
  }

  async dispose() {
    await super.dispose();

    // no special dispose needed for supportList (pure element)

    //override if needed
  }

  getQuoteStateEngine(): IUserQuoteStateEngine {
    // In fabricator mode, the fabricator IS the supplier — skip supplier review process
    // so quotes can go directly Issued → Approved without Accept/SupplierReview steps
    const useSupplierReview = userDataStore.isFabricatorMode
      ? false
      : !this.supplierQuoteConfig
        ? true
        : this.supplierQuoteConfig.allowSupplierReview;
    return getQuoteStateEngine(this.quoteContainerManager, useSupplierReview);
  }

  addSupplierMenuItems(childEvents: TabAction[]) {
    super.addSupplierMenuItems(childEvents);
    const buttons = this.getStandardButtons();
    if (v6SupportsVersion(v6VersionMap.hasCheckSupplierQuote)) childEvents.push(buttons.createSupplierQuote);
  }

  addReportMenuItems(_childEvents: TabAction[]): void {
    _childEvents.push(...this._reportMenuItems);
  }

  async buildReportMenuItems(): Promise<TabAction[]> {
    const childEvents: TabAction[] = [];
    super.addReportMenuItems(childEvents);

    // From issue onwards the reports are a stored record of the issued quote, so the menu
    // downloads them instead of generating them.
    const quoteState = this.quoteContainer.quote?.state;
    const reportsAreStored =
      quoteState === QuoteState.Issued ||
      quoteState === QuoteState.Accepted ||
      quoteState === QuoteState.Accepted_AssignedToReviewer ||
      quoteState === QuoteState.Approved ||
      quoteState === QuoteState.Accepted_RefusedBySupplier ||
      quoteState === QuoteState.Rejected ||
      quoteState === QuoteState.Cancelled ||
      quoteState === QuoteState.Lapsed;

    //[0] Should Render Template
    //[1] Template to Render
    //[2] Disable Generate Report action
    const getReportButtonTemplate = async (
      documentSubType: DocumentSubType
    ): Promise<[boolean, TemplateResult, boolean]> => {
      let src: string | null = '#';

      let caption: Snippet;

      const validateDocumentSubType = (documentSubType: DocumentSubType): void => {
        switch (documentSubType) {
          case DocumentSubType.ClientQuoteAPDF:
          case DocumentSubType.ClientQuoteBNoItemPricePDF:
          case DocumentSubType.ClientQuoteCNoItemPriceNoDimPDF:
          case DocumentSubType.ClientQuoteGNoPricePDF:
          case DocumentSubType.DealerQuoteEIternalPDF:
          case DocumentSubType.DealerQuoteHIternalPDF:
          case DocumentSubType.ClientQuoteSummaryPDF:
            // Valid document subtypes, do nothing
            break;
          default:
            throw new Error('Invalid document subtype');
        }
      };
      const getCaptionForDocumentSubType = (documentSubType: DocumentSubType, notGenerated: boolean): Snippet => {
        let caption: Snippet;

        switch (documentSubType) {
          case DocumentSubType.ClientQuoteAPDF:
            caption = LangFranchiseeQuoteView.clientQuoteAPDF(notGenerated);
            break;
          case DocumentSubType.ClientQuoteBNoItemPricePDF:
            caption = LangFranchiseeQuoteView.clientQuoteBNoItemPricePDF(notGenerated);
            break;
          case DocumentSubType.ClientQuoteCNoItemPriceNoDimPDF:
            caption = LangFranchiseeQuoteView.clientQuoteCNoItemPriceNoDimPDF(notGenerated);
            break;
          case DocumentSubType.ClientQuoteGNoPricePDF:
            caption = LangFranchiseeQuoteView.clientQuoteGNoPricePDF(notGenerated);
            break;
          case DocumentSubType.DealerQuoteEIternalPDF:
            caption = LangFranchiseeQuoteView.dealerQuoteEIternalPDF(notGenerated);
            break;
          case DocumentSubType.DealerQuoteHIternalPDF:
            caption = LangFranchiseeQuoteView.dealerQuoteHIternalPDF(notGenerated);
            break;
          case DocumentSubType.ClientQuoteSummaryPDF:
            caption = LangFranchiseeQuoteView.clientQuoteSummaryPDF(notGenerated);
            break;
          default:
            throw Error('Not a quote report request');
        }

        return caption;
      };

      validateDocumentSubType(documentSubType);
      if (reportsAreStored) {
        src = await getPermanentProjectDocumentURL(
          cache().projectResourceLink.getLocalData(this.quoteContainer.quoteId)?.projectId ?? emptyGuid,
          this.quoteContainer.quoteId,
          ResourceType.Quote,
          documentSubType,
          true
        );

        if (src === null) {
          src = '#';
          caption = getCaptionForDocumentSubType(documentSubType, true);
        } else {
          caption = getCaptionForDocumentSubType(documentSubType, false);
        }

        return src === '#'
          ? [false, html`${caption}`, true]
          : [
              true,
              html` <webmodule-button variant="text" class="report-dropdown-button" href="${src}" target="_blank"
                >${caption}
              </webmodule-button>`,
              true
            ];
      } else caption = getCaptionForDocumentSubType(documentSubType, false);

      return [this.quoteContainer.quote?.state === QuoteState.Active, html`${caption}`, false];
    };

    // A data export is never generated on demand. It is written once, when the quote is issued,
    // and is a permanent record of that moment, so regenerating it would break the guarantee the
    // file exists to make. The entry therefore has no generate action behind it and simply does
    // not appear until the stored file is there, which is also how a quote issued before this
    // feature shipped behaves.
    const getDataExportTemplate = async (
      documentSubType: DocumentSubType,
      caption: Snippet
    ): Promise<TemplateResult | null> => {
      const src = await getPermanentProjectDocumentURL(
        cache().projectResourceLink.getLocalData(this.quoteContainer.quoteId)?.projectId ?? emptyGuid,
        this.quoteContainer.quoteId,
        ResourceType.Quote,
        documentSubType,
        true
      );

      return src === null
        ? null
        : html` <webmodule-button variant="text" class="report-dropdown-button" href="${src}" target="_blank"
            >${caption}
          </webmodule-button>`;
    };

    // The quote reports in catalogue order. An Active quote offers the ones the supplier has made
    // available to its dealers; from issue onwards every stored report is offered, whatever the
    // supplier has changed since, so a quote keeps the documents it was issued with. A supplier
    // config without the list (an older API) falls back to the reports offered before suppliers
    // could choose.
    const quoteReports: { report: DealerReport; documentSubType: DocumentSubType }[] = [
      { report: DealerReport.ReportA, documentSubType: DocumentSubType.ClientQuoteAPDF },
      { report: DealerReport.ReportB, documentSubType: DocumentSubType.ClientQuoteBNoItemPricePDF },
      { report: DealerReport.ReportC, documentSubType: DocumentSubType.ClientQuoteCNoItemPriceNoDimPDF },
      { report: DealerReport.ReportG, documentSubType: DocumentSubType.ClientQuoteGNoPricePDF },
      { report: DealerReport.ReportJ, documentSubType: DocumentSubType.ClientQuoteSummaryPDF },
      { report: DealerReport.ReportE, documentSubType: DocumentSubType.DealerQuoteEIternalPDF },
      { report: DealerReport.ReportH, documentSubType: DocumentSubType.DealerQuoteHIternalPDF }
    ];
    const supplierReports = this.supplierQuoteConfig?.quoteReports;
    const isOffered = (report: DealerReport): boolean =>
      reportsAreStored ||
      (supplierReports ? supplierReports.some(x => x.report === report && x.enabled) : report !== DealerReport.ReportH);

    const reportEntries = await Promise.all(
      quoteReports
        .filter(x => isOffered(x.report))
        .map(async x => ({ report: x.report, template: await getReportButtonTemplate(x.documentSubType) }))
    );

    const quoteExportJsonTemplate = await getDataExportTemplate(
      DocumentSubType.DealerQuoteDataExportJson,
      LangFranchiseeQuoteView.quoteDataExportJson()
    );
    const quoteExportXmlTemplate = await getDataExportTemplate(
      DocumentSubType.DealerQuoteDataExportXml,
      LangFranchiseeQuoteView.quoteDataExportXml()
    );

    for (const { report, template } of reportEntries) {
      const [shouldRender, caption, disableGenerate] = template;
      if (shouldRender)
        childEvents.push({
          caption: () => caption,
          onClick: async () => {
            if (!disableGenerate) await this.generateReportV2(report);
          }
        });
    }

    // Download only: no onClick, because there is nothing to generate.
    if (quoteExportJsonTemplate) childEvents.push({ caption: () => quoteExportJsonTemplate });
    if (quoteExportXmlTemplate) childEvents.push({ caption: () => quoteExportXmlTemplate });

    const buttons = this.getStandardButtons();
    if (!this.isLockedFromUse)
      switch (this.quoteContainerManager.quote.state) {
        case QuoteState.Active:
          if (this._canCreateShopDrawingPreview) childEvents.push(buttons.previewShopDrawing);
          break;
        case QuoteState.SupplierReviewPending:
          if (this._canCreateShopDrawingPreview) childEvents.push(buttons.previewShopDrawing);
          break;
        default:
          break;
      }

    //No permanent document
    if (this._permanentShopDrawingUrl) {
      childEvents.push({
        caption: () =>
          html` <webmodule-button
            variant="text"
            class="report-dropdown-button"
            href="${this._permanentShopDrawingUrl || ''}"
            target="_blank"
          >
            ${LangFranchiseeQuoteView.shopDrawingPlural()}
          </webmodule-button>`,
        onClick: () => {}
      });
    }

    if (childEvents.length > 0)
      childEvents.unshift({
        heading: true,
        caption: () =>
          reportsAreStored ? LangFranchiseeQuoteView.downloadReports() : LangFranchiseeQuoteView.generateReports()
      });

    return childEvents;
  }

  protected requiresSSIRecalculation(): boolean {
    return this.quoteContainerManager.quoteAttributesRequireSSIRecalculation();
  }

  public async afterConstruction(): Promise<void> {
    await this.quoteContainerManager.needsQuote();
    await this.quoteContainerManager.needsQuoteItems();
    await this.quoteContainerManager.needsQuoteSupport();

    await super.afterConstruction();
    // In fabricator mode, disable supplier review so that the Accept button
    // is hidden and the Approve button goes directly from Issued → Approved
    if (userDataStore.isFabricatorMode && this.supplierQuoteConfig) {
      this.supplierQuoteConfig = { ...this.supplierQuoteConfig, allowSupplierReview: false };
    }
    this.quoteContainerManager.afterSave.push(async () => {
      const siblingIds = this.quoteContainerManager.siblings?.map(x => x.quoteId) ?? [];
      await Promise.all([
        this.prlCache.updateLocal(this.quote.id, ...siblingIds),
        cache().quote.updateLocal(this.quote.id, ...siblingIds)
      ]);
      // The save runs an SSI round-trip that replaces the calculation values, and Save is in
      // the tab manager's overall button region, so it can be pressed while this page is the
      // active one. Without this the page keeps rendering the pre-save numbers until the user
      // switches away and back (onEnter is its only other refresh).
      await this.calculationsView?.refreshData();
      await this.loadExternalRelatedData();
    });
    await this.prlCache.get(this.quote.id);

    if (
      currentUserClaims().isAgent &&
      this.quoteContainerManager.quoteState === QuoteState.Accepted &&
      !this.quote.supplierApproved
    ) {
      //we are a reviewer coming to look at the completedItem.
      await this.quoteContainerManager.quoteStateChange(QuoteState.Accepted_AssignedToReviewer);
    }
    await this.loadExternalRelatedData();
  }

  async produceShopDrawing() {
    if (this.quoteContainerManager.isV6)
      await V6SupplierQuoteBase.generateShopDrawing(
        this.branchQuote?.branchId ?? emptyGuid,
        this.quote.id,
        await this.quoteContainerManager.projectId()
      );
  }

  async produceSupplierQuote() {
    if (this.quoteContainerManager.isV6)
      if (!v6SupportsVersion(v6VersionMap.hasCheckSupplierQuote)) {
        await information(LangFranchiseeQuoteView.frameConfigRequired());
      }
    await V6SupplierQuoteBase.generateTempQuote(
      this.branchQuote?.branchId ?? emptyGuid,
      this.quote.id,
      this.quote.supplierId
    );
  }

  /**
   *
   * @returns override this to add logic
   */
  async mustValidateItems(): Promise<boolean> {
    return await this.providerInterface.mustValidateItems(this.quoteContainerManager);
  }

  /**
   * override this to add logic
   */
  async pricingNeedsUpdate(): Promise<boolean> {
    return await this.providerInterface.pricingNeedsUpdate(this.quoteContainerManager);
  }

  /**
   * override this to add logic
   */
  async validateOutOfDateQuoteItems() {
    const doReValidate = this.isDataReadonly();

    const result = doReValidate
      ? await this.providerInterface.reValidateOutOfDateQuoteItemsOnLockedQuote(this.quoteContainerManager)
      : await this.providerInterface.validateOutOfDateQuoteItems(this.quoteContainerManager);
    this.dispatchUiChanged();
    return result;
  }

  public getTitle(): Snippet {
    //override and do not use this.title

    const quote = this.quoteContainer.quote;
    if (!quote) return html``; // will never happen

    const projectId = this._projectId;
    const orderId = this._orderId;

    const projectTemplate = validId(projectId)
      ? html`<a href="${resolveURL(resourceProject, projectId)}" class="btn btn-sm btn-light"
          >${this._projectCacheDisplay ?? '???-???-???'}</a
        >`
      : html``;
    const orderTemplate = validId(orderId)
      ? html`<a href="${resolveURL(resourcePurchaseOrder, orderId)}" class="btn btn-sm btn-light"
          >${this._orderCacheDisplay ?? '???-???-???'}</a
        >`
      : html``;

    return html`
      ${getQuoteTitle(quote, this.quoteContainerManager.quoteState)}
      <div class="title-links">${projectTemplate} ${orderTemplate}</div>
      ${this._supplierTitle}
    `;
  }

  async valid(_state: QuoteState): Promise<boolean> {
    return await this.checkValidations();
  }

  async canCompleteReview(): Promise<boolean> {
    return await this.quoteContainerManager.canIssueQuote();
  }

  public override async prepareForSave(): Promise<void> {
    await super.prepareForSave();

    if (this.isDataReadonly()) return;

    await this.termsView?.prepareForSave();
  }

  async launchItemByConversationId(conversationId: string): Promise<void> {
    const conversationCache = cache().quoteItemConversation;
    await this.quoteContainerManager.needsQuoteItems();
    const specialOrderItems = [
      ...this.quoteContainerManager.liveItems().filter(qi => isSpecialItem(qi)),
      ...(this.quoteContainerManager.container.deletedItems?.filter(qi => isSpecialItem(qi)) ?? [])
    ];

    const conversaionLinks = await conversationCache.getMany(specialOrderItems.map(x => x.id));
    let needsSupportRequestOpened = true;
    if (conversaionLinks) {
      const qic = conversaionLinks.find(cl => {
        return cl.data.items.find(c => c.conversationId === conversationId) !== undefined;
      });
      if (qic) {
        needsSupportRequestOpened = false;
        await this.runQuoteItemAction(this.quoteContainerManager.quoteItemContainer(qic.id), quoteItemAction.edit);
      }
    }
    if (needsSupportRequestOpened) {
      const result = await this.quoteContainerManager.needsQuoteSupport();
      //try to find the support request and open it.

      if (result) {
        const supportRequest = result.items.find(i => i.conversationId === conversationId);
        if (supportRequest) {
          await this.openSupportTicket(supportRequest, true);
        }
      }
    }
  }
  async setActiveTabByHash(): Promise<void> {
    const hashId = window.location.hash;
    if (hashId.startsWith('#support_')) {
      const conversationId = hashId.substring(9);
      await this.launchItemByConversationId(conversationId);
    } else {
      await super.setActiveTabByHash();
    }
  }

  protected async supportTicketAlreadyOpen(): Promise<boolean> {
    const alreadyOpen = this.supportTicketPagesOpenCount >= this.maxItemPageAllowed;
    if (alreadyOpen) await information(LangFranchiseeQuoteView.closeExistingSupportRequestEditor());
    return alreadyOpen;
  }

  // openSupportTicketHandler removed – logic merged into onSupportTicketOpen

  protected customButtons(state: QuoteState): TabAction[] {
    // eslint-disable-next-line no-bitwise
    if (this.showSupportRequestButton() && flagInSet(state, QuoteState.Active | QuoteState.SupplierReviewPending)) {
      const addSupportTicket: TabAction = {
        caption: () => LangFranchiseeQuoteView.requestSupport(),
        onClick: async () => {
          await this.createSupportTicket(BranchQuoteSupportItemType.QuoteAssistanceRequest);
        },
        disabled: this.isDataReadonly()
      };
      return [addSupportTicket];
    }
    return [];
  }

  protected doAddPages(pages: TabPage[]) {
    if (this.quote.state !== QuoteState.Draft) {
      const page = this.createSupportPage();
      if (page) pages.push(page);
    }

    pages.push(this.createCalculationsPage());
    pages.push(this.createTermsPage());
  }

  protected async internalOpenQuoteItemForEdit(quoteItemContainer: QuoteItemContainer): Promise<void> {
    //this is a special case line completedItem that is not editable and launches a modal dialog
    if (isDealerQuotePriceAdjustment(quoteItemContainer.item)) {
      await (this.itemsView as FranchiseeQuoteItemsView).eventRunPriceAdjustmentModal();
    } else {
      const tickets = await this.quoteContainerManager.hasActiveSupportTickets(quoteItemContainer.item.id);
      if (tickets.success) {
        let msg = '';
        for (const t of tickets.items) {
          msg += `- ${t.subject} (${getSupportTypeStatus(t.status)})\n`;
          //show warning.
        }
        if (
          !(await AskConfirmation(
            LangFranchiseeQuoteView.openQuoteItemWithActiveSupportRequests({ tickets: msg.trim() })
          ))
        )
          return;
      }
      await super.internalOpenQuoteItemForEdit(quoteItemContainer);
    }
  }

  protected async internalOpenQuoteItemPropertyDialog(quoteItemContainer: QuoteItemContainer): Promise<void> {
    if (isDealerQuotePriceAdjustment(quoteItemContainer.item)) {
      await (this.itemsView as FranchiseeQuoteItemsView).eventRunPriceAdjustmentModal();
    } else await super.internalOpenQuoteItemPropertyDialog(quoteItemContainer);
  }

  protected async quoteItemViewFactory(
    quoteItemContainer: QuoteItemContainer | null,
    providerType: string,
    contentType: number,
    supplierSelector?: unknown
  ): NullPromise<QuoteItemView> {
    //these are the default non-provider line items
    switch (contentType) {
      case quoteItemContentType.specialItem: {
        const editor = await constructAsync(
          new EngineeredToOrderLineItemViewMerged(await this.getQuoteItemViewOptions(quoteItemContainer))
        );
        editor.supplierSelector = supplierSelector as V6CustomItemRequestType;
        return editor;
      }
      default:
        return await super.quoteItemViewFactory(quoteItemContainer, providerType, contentType, supplierSelector);
    }
  }

  protected async internalDeleteQuoteItem(quoteItemContainer: QuoteItemContainer): Promise<void> {
    if (!isSupplierUsingSSI(this.quote.supplierId)) {
      await super.internalDeleteQuoteItem(quoteItemContainer);
      return;
    }

    const processSSIDelete = async () => {
      const qm = this.quoteContainerManager;
      if ((qm.isV6 && isSupplierFrameItem(quoteItemContainer.item)) || isSpecialItem(quoteItemContainer.item)) {
        const foundPage = this.findIfPageAvailable(quoteItemContainer);
        if (foundPage) await this.closePageIfAvailable(quoteItemContainer);

        const informationDispatcher = new InformationDispatcher();
        const setInfo = async (s: string, header = LangFranchiseeQuoteView.preparingToDelete()) => {
          await informationDispatcher.setInformation(`# ${header}

                + ${s}`);
        };
        await setInfo(LangFranchiseeQuoteView.gettingUpdatesFromSupplier());

        const saveModal = new SaveWorkflowModal(
          LangFranchiseeQuoteView.deletingQuoteItem({ title: quoteItemContainer.item.title }),
          informationDispatcher,
          3
        );

        //we dont want to wait for the modal to finish.
        const modalState = await saveModal.show();
        await modalState.onShow;
        try {
          //build an ssi processor that will build the quote exluding the deleted completedItem, so that we get data
          //based on its non-existence
          const ssiProcessor = this.quoteContainerManager.ssiProcessor([quoteItemContainer.item.id]);
          const ssiInputs = await ssiProcessor.processSSI();
          await setInfo(LangFranchiseeQuoteView.supplierProcessingComplete());

          if (await qm.deleteQuoteItem(quoteItemContainer, ssiInputs)) {
            await setInfo(LangFranchiseeQuoteView.quoteItemDeleted());
          } else {
            await setInfo(LangFranchiseeQuoteView.unableToDeleteSelectedQuoteItem());
            fireQuickErrorToast(LangFranchiseeQuoteView.unableToDeleteSelectedQuoteItem());
          }
        } finally {
          saveModal?.done();
          await saveModal?.hideModal();
        }
      } else {
        await super.internalDeleteQuoteItem(quoteItemContainer);
      }
    };
    await processSSIDelete();
  }

  protected async loadExternalRelatedData() {
    this._orderId = await this.quoteContainerManager.purchaseOrderId();
    this._projectId = await this.quoteContainerManager.projectId();
    this._projectCacheDisplay = (await this.projectCache.get(this._projectId))?.displayValue;
    this._orderCacheDisplay = (await this.purchaseOrderCache.get(this._orderId))?.displayValue;
    this._canCreateShopDrawingPreview = await this.canCreateShopDrawingPreview();

    this._permanentShopDrawingUrl = await getPermanentProjectDocumentURL(
      cache().projectResourceLink.getLocalData(this.quoteContainerManager.quote.id)?.projectId ?? emptyGuid,
      this.quoteContainerManager.quote.id,
      ResourceType.Quote,
      DocumentSubType.QuoteShopDrawing,
      true
    );
    this._reportMenuItems = await this.buildReportMenuItems();
    const supplierTitle = async () => {
      const isMulti = isMultiSupplier();
      const supplierId = this.quoteContainer.quote?.supplierId ?? emptyGuid;
      const supplierName =
        isMulti && this.quoteContainer.quote
          ? ((await getQuoteSuppliers(false)).find(x => x.supplierId === supplierId)?.description ?? '')
          : '';

      return isMulti
        ? html`
            <div>
              <span class="subtitle text-secondary fs-3">[${supplierName}]</span>
            </div>
          `
        : html``;
    };
    this._supplierTitle = await supplierTitle();
    this.dispatchUiChanged();
  }

  protected async canCreateShopDrawingPreview(): Promise<boolean> {
    return (await this.quoteContainerManager.projectId()) !== emptyGuid && this.quoteContainerManager.hasFrames;
  }

  protected getQuoteViewChildFactory(): QuoteViewChildFactory {
    return new FranchiseeQuoteViewChildFactory(this);
  }

  protected async canAddItemFrame(): Promise<boolean> {
    const qm = this.quoteContainerManager;
    if (isEmptyOrSpace(qm.branchQuote.clientTypeId)) {
      await information(LangFranchiseeQuoteView.pricingBasedOnPaymentProfile());
      return false;
    }
    return true;
  }

  protected includeShippingLineItem(): boolean {
    return isValidV6ConfigVersion()
      ? v6Config().getSupplier(this.quote.supplierId).options?.['includeShipping'] === 'true'
      : false;
  }

  protected hasSupplierGlassView(): boolean {
    return isValidV6ConfigVersion()
      ? v6Config().getSupplier(this.quote.supplierId).options?.['useQuoteGlass'] === 'true'
      : false;
  }

  get supportItemsViewPageIndex() {
    let pageindex = 0;
    this.tabManager.pages.some((page, index) => {
      const found = page.data === this.supportList;
      if (found) pageindex = index;
      return found;
    });
    return pageindex;
  }

  async tabDeletedEvent(e: TabCloseEvent): Promise<void> {
    if (e.page.data instanceof FranchiseeQuoteSupportTicketView) {
      e.newIndex = this.supportItemsViewPageIndex;

      return;
    }
    await super.tabDeletedEvent(e);
  }
  protected async onQuoteStateChanged(state: QuoteState): Promise<void> {
    // Frontend-orchestrated fabrication row creation on quote approval.
    // After QuoteService approves the quote, create QuoteItemFabrication rows
    // for eligible Fabricated Standard Frame items via FabricationService.
    // This is safe because CreateQuoteItemFabrications is idempotent.
    if (state === QuoteState.Approved && userDataStore.isFabricatorMode) {
      await this.createFabricationRowsForApprovedQuote();
    }

    if (
      state === QuoteState.Accepted ||
      state === QuoteState.Approved ||
      state === QuoteState.Cancelled ||
      state === QuoteState.IssuePending ||
      state === QuoteState.Rejected ||
      state === QuoteState.SupplierReviewed
    ) {
      goURL('project', await this.quoteContainerManager.projectId(), 'resources');
    } else if (state === QuoteState.Active) {
      const page = this.createSupportPage();

      if (page) this.tabManager.addPage(page, false);
    }
  }

  private createSupportPage(): TabPage | null {
    if (!this.showSupportRequestPage()) return null;
    return {
      caption: () => LangFranchiseeQuoteView.supplierSupport(),
      canClose: () => false,
      canLeave: () => true,
      onEnter: () => {
        this.supportList?.refreshData();
        return Promise.resolve();
      },
      content: () => this.supportList ?? document.createElement('div'),
      data: this.supportList,
      fragment: 'support'
    };
  }

  private onSupportTicketOpen(e: CustomEvent<{ id: string; links?: { quoteItemId: string }[] }>) {
    const ticketId = e.detail.id;
    // If ETO linked, route to quote item editor instead of opening ticket tab.
    const links = e.detail.links || [];
    const etoLink = links.find(l => this.quoteItems.some(qi => qi.id === l.quoteItemId));
    if (etoLink) {
      const qItem = this.quoteItems.find(qi => qi.id === etoLink.quoteItemId);
      if (qItem) {
        void this.runQuoteItemAction(this.quoteContainerManager.quoteItemContainer(qItem.id), quoteItemAction.edit);
        return;
      }
    }
    // Fallback: open or focus ticket tab (fetch ticket if not already known)
    void (async () => {
      const supportData = await this.quoteContainerManager.needsQuoteSupport();
      const ticket = supportData?.items.find(i => i.id === ticketId);
      if (ticket) {
        this.openSupportTicket(ticket);
      }
    })();
  }

  // The V6 quote calculation values. The page hides itself entirely when the supplier
  // returned no calculation groups, so quotes without them show no empty tab.
  private createCalculationsPage(): TabPage {
    return {
      caption: () => LangFranchiseeQuoteView.quoteCalculations(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: async () => {
        await this.calculationsView?.refreshData();
      },
      content: () => {
        return this.calculationsView?.ui ?? document.createElement('div');
      },
      hidden: () => !this.quoteContainerManager.hasQuoteCalculationValues,
      data: this.calculationsView,
      fragment: 'calculations'
    };
  }

  private createTermsPage(): TabPage {
    return {
      caption: () => LangFranchiseeQuoteView.termsConditions(),
      canClose: () => false,
      canLeave: async () => await this.allowPageSwitch(),
      onEnter: () => {
        this.termsView?.refreshData();
        return Promise.resolve();
      },
      secondaryButtons: () => this.termsView?.buttonMenu() ?? html``,
      content: () => {
        return this.termsView?.ui ?? document.createElement('div');
      },
      data: this.termsView,
      fragment: 'terms'
    };
  }

  private showSupportRequestPage(): boolean {
    // Hide "Supplier Support" tab entirely in fabricator mode
    if (userDataStore.isFabricatorMode) return false;
    // Default to true if missing/not set
    if (this.supplierQuoteConfig === undefined) return true;

    return (
      this.supplierQuoteConfig.allowSupportRequest ||
      this.supplierQuoteConfig.allowSupplierReview ||
      this.quoteContainerManager.hasQuoteSupport
    );
  }

  private showSupportRequestButton(): boolean {
    // Hide "Request Support" button in fabricator mode
    if (userDataStore.isFabricatorMode) return false;
    // Default to true if missing/not set
    if (this.supplierQuoteConfig === undefined) return true;

    return this.supplierQuoteConfig.allowSupportRequest;
  }

  /**
   * Frontend-orchestrated fabrication row creation after quote approval.
   * Filters for eligible Fabricated Standard Frame items and calls FabricationService.
   * Idempotent — safe to retry if the first call fails.
   */
  private async createFabricationRowsForApprovedQuote(): Promise<void> {
    try {
      const quoteItems = this.quoteContainerManager.quoteItems;
      const eligibleIds = quoteItems
        .filter(
          qi =>
            qi.serviceProvider.toLowerCase() === 'v6' &&
            qi.quoteItemContentType === fabricatedStandardFrameContentType &&
            qi.state === QuoteItemState.Active
        )
        .map(qi => qi.id);

      if (eligibleIds.length > 0) {
        await getApiFactory().fabricationJob().createQuoteItemFabrications({
          quoteId: this.quoteContainerManager.quoteId,
          quoteItemIds: eligibleIds
        });
      }
    } catch {
      // Non-blocking — self-healing on next page load
    }
  }
}

export class FranchiseeQuoteView extends QuoteView {
  protected createView(): QuoteDataEntryView {
    return new FranchiseeQuoteDEView(this.options, this);
  }
}
